/**
 * The single path through which the browser talks to the API. There is no direct
 * database client in the browser at all: every read is a purpose-built endpoint
 * response, so internal VGS information is absent from the payload rather than
 * merely hidden by the UI.
 */

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string>;

  constructor(status: number, code: string, message: string, details?: Record<string, string>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/**
 * Field-level validation problems are folded into the message so a generic
 * "Request validation failed." banner always names the offending fields.
 */
function withFieldDetail(message: string, details?: Record<string, string>): string {
  if (!details) return message;
  const fields = Object.entries(details).map(([field, problem]) =>
    field === '(root)' ? problem : `${humanizeField(field)}: ${problem}`,
  );
  return fields.length > 0 ? `${message} ${fields.join('; ')}` : message;
}

function humanizeField(field: string): string {
  const words = field.split('.').pop() ?? field;
  const spaced = words.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: Record<string, string> };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  signal?: AbortSignal;
}

/** Set by the app shell so a 401 anywhere returns the user to the sign-in screen. */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

function buildUrl(path: string, queryParams: RequestOptions['query']): string {
  if (!queryParams) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(queryParams)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) search.append(key, entry);
    } else {
      search.set(key, String(value));
    }
  }
  const queryString = search.toString();
  return queryString.length > 0 ? `${path}?${queryString}` : path;
}

export async function apiRequest<Result>(
  path: string,
  options: RequestOptions = {},
): Promise<Result> {
  const method = options.method ?? 'GET';

  const headers: Record<string, string> = {
    // Cannot be set by a cross-origin form post, which is what makes it a usable
    // CSRF guard for a cookie-authenticated API.
    'X-Requested-With': 'vgsusps-web',
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 401) {
    onUnauthenticated?.();
    throw new ApiRequestError(401, 'unauthorized', 'Your session has ended. Please sign in again.');
  }

  const contentType = response.headers.get('Content-Type') ?? '';

  if (!response.ok) {
    let code = 'error';
    let message = `Request failed (${response.status})`;
    let details: Record<string, string> | undefined;

    if (contentType.includes('application/json')) {
      const parsed = (await response.json().catch(() => null)) as ApiErrorBody | null;
      code = parsed?.error?.code ?? code;
      message = parsed?.error?.message ?? message;
      details = parsed?.error?.details;
    }
    throw new ApiRequestError(response.status, code, withFieldDetail(message, details), details);
  }

  if (response.status === 204) return undefined as Result;
  if (contentType.includes('application/json')) return (await response.json()) as Result;
  return (await response.text()) as Result;
}

/** Triggers a browser download for an endpoint that returns a file. */
export async function apiDownload(
  path: string,
  fallbackFileName: string,
  query?: RequestOptions['query'],
): Promise<void> {
  const response = await fetch(buildUrl(path, query), {
    headers: { 'X-Requested-With': 'vgsusps-web' },
    credentials: 'same-origin',
  });

  if (response.status === 401) {
    onUnauthenticated?.();
    throw new ApiRequestError(401, 'unauthorized', 'Your session has ended. Please sign in again.');
  }
  if (!response.ok) {
    throw new ApiRequestError(response.status, 'error', 'The download could not be completed.');
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  const fileName = match?.[1] ?? fallbackFileName;

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Reads a File into base64 for the JSON upload endpoint. */
export async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return btoa(binary);
}
